import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getSessionId() {
  let id = localStorage.getItem("chat_session_id");
  if (!id) {
    id = (crypto.randomUUID && crypto.randomUUID()) || `s_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    localStorage.setItem("chat_session_id", id);
  }
  return id;
}

const WELCOME = "Hey there! 👋 I'm 360 Tech. Looking for a new website or to get found on Google? Tell me a bit about your business and I'll point you in the right direction!";

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([{ id: "welcome", role: "assistant", content: WELCOME }]);
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());
  const msgId = useRef(0);
  const nextId = () => `m${++msgId.current}`;

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    const botId = nextId();
    setMessages((m) => [...m, { id: nextId(), role: "user", content: text }, { id: botId, role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages((m) => m.map((msg) => (msg.id === botId ? { ...msg, content: msg.content + chunk } : msg)));
      }
    } catch (e) {
      setMessages((m) => m.map((msg) => (msg.id === botId ? { ...msg, content: "Sorry, I'm having trouble right now. Please use the contact form and we'll reach out!" } : msg)));
    } finally {
      setStreaming(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      <button onClick={() => setOpen(!open)} aria-label="Open chat"
        className="fixed bottom-20 right-6 md:bottom-6 z-[60] h-14 w-14 grid place-items-center rounded-full bg-[#1E90FF] text-white shadow-lg shadow-[#1E90FF]/40 hover:scale-105 transition-transform"
        data-testid="chatbot-toggle">
        {open ? <X size={24} /> : <MessageSquare size={24} />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 20, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-36 right-6 md:bottom-24 z-[60] w-[calc(100vw-3rem)] sm:w-96 h-[30rem] flex flex-col bg-[#0d0d0d] border border-white/15 rounded-lg overflow-hidden shadow-2xl"
            data-testid="chatbot-window">
            <div className="bg-[#111] border-b border-white/10 px-5 py-4 flex items-center gap-3">
              <span className="h-9 w-9 grid place-items-center rounded-full bg-[#1E90FF]/15 text-[#1E90FF] font-display font-black">360</span>
              <div>
                <p className="font-semibold text-sm">360 Tech Assistant</p>
                <p className="text-xs text-[#22C55E] flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-[#22C55E] pulse-dot" /> Online now</p>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chatbot-messages">
              {messages.map((m, i) => (
                <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[82%] px-4 py-2.5 text-sm leading-relaxed rounded-lg ${m.role === "user" ? "bg-[#1E90FF] text-white rounded-br-sm" : "bg-[#1a1a1a] text-white/90 border border-white/10 rounded-bl-sm"}`}>
                    {m.content || (streaming && i === messages.length - 1 ? <span className="text-white/40">…</span> : "")}
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-white/10 p-3 flex items-center gap-2">
              <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKey} placeholder="Type your message..."
                className="flex-1 bg-black border border-white/15 rounded-sm px-3 py-2.5 text-sm text-white placeholder-white/30 focus:border-[#1E90FF] focus:outline-none"
                data-testid="chatbot-input" />
              <button onClick={send} disabled={streaming} className="h-10 w-10 grid place-items-center rounded-sm bg-[#22C55E] text-black hover:bg-[#1fb455] transition-colors disabled:opacity-50" data-testid="chatbot-send">
                <Send size={18} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
